/**
 * Multi-Step Wizard Component
 *
 * Renders a wizard UI for MULTI_STEP interrupts, allowing users to
 * complete multiple steps (multiple choice or confirmation) in sequence.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  clearAllActivities,
  clearInterrupt,
  setAssistantStatus,
  setCurrentStepIndex,
  setStepResponse,
} from '../store/chatSlice';
import { selectInterrupt } from '../store/selectors';
import type { ConfirmationStepPayload, MultipleChoiceStepPayload, StepPayload } from '../types';

interface MultiStepWizardProps {
  /**
   * Callback when wizard is completed with all step responses
   */
  onComplete: (responses: Record<string, { selected_ids?: string[]; confirmed?: boolean }>) => void;
  /**
   * Callback when wizard is dismissed
   */
  onDismiss?: () => void;
  /**
   * Whether wizard is processing
   */
  isProcessing?: boolean;
}

export const MultiStepWizard: React.FC<MultiStepWizardProps> = ({
  onComplete,
  onDismiss,
  isProcessing = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteId } = useParams<{ noteId: string }>();
  const { note: noteMetrics } = useEventTracking();
  const interrupt = useAppSelector(selectInterrupt);

  if (!interrupt || interrupt.eventType !== 'MULTI_STEP' || !interrupt.steps) {
    return null;
  }

  const { steps, title, currentStepIndex = 0, stepResponses = {} } = interrupt;
  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Get current step response
  const currentStepResponse = stepResponses[currentStep.id];

  // Handle step navigation
  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      dispatch(setCurrentStepIndex(currentStepIndex - 1));
    }
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      dispatch(setCurrentStepIndex(currentStepIndex + 1));
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    dispatch(clearInterrupt());
    dispatch(clearAllActivities());
    dispatch(setAssistantStatus('idle'));
    onDismiss?.();
  };

  // Helper to get step type (supports both camelCase and snake_case)
  const getStepType = (step: StepPayload): string => {
    return step.stepType || (step as any).step_type || '';
  };

  // Handle completion - collect all responses and submit
  const handleComplete = () => {
    // Build response object keyed by step ID
    const responses: Record<string, { selected_ids?: string[]; confirmed?: boolean }> = {};

    steps.forEach(step => {
      const response = stepResponses[step.id];
      const stepType = getStepType(step);
      if (response) {
        if (stepType === 'multiple_choice' && response.selectedIds) {
          responses[step.id] = { selected_ids: response.selectedIds };
        } else if (stepType === 'confirmation' && response.confirmed !== undefined) {
          responses[step.id] = { confirmed: response.confirmed };
        }
      }
    });

    onComplete(responses);
  };

  // Render multiple choice step
  const renderMultipleChoiceStep = (step: StepPayload) => {
    // Type assertion - we know this is a multiple choice step
    const mcStep = step as MultipleChoiceStepPayload;
    const selectedIds = currentStepResponse?.selectedIds || [];
    const options = mcStep.options || [];
    const allSelected = options.length > 0 && selectedIds.length === options.length;
    
    // Support both camelCase and snake_case
    const allowMultiple = mcStep.allowMultiple ?? (mcStep as any).allow_multiple ?? true;
    const minSelections = mcStep.minSelections ?? (mcStep as any).min_selections;
    const maxSelections = mcStep.maxSelections ?? (mcStep as any).max_selections;
    const allowSelectAll = mcStep.allowSelectAll ?? (mcStep as any).allow_select_all;

    const handleToggle = (optionId: string, optionLabel: string) => {
      // Track suggested action click
      if (noteId) {
        noteMetrics.chatSuggestedActionClicked?.(noteId, {
          action_type: 'section_selection',
          action_id: optionId,
          action_label: optionLabel,
          note_id: noteId,
        });
      }

      let newSelectedIds: string[];
      if (!allowMultiple) {
        // Single selection mode
        newSelectedIds = [optionId];
      } else {
        // Multi-selection mode - toggle
        if (selectedIds.includes(optionId)) {
          newSelectedIds = selectedIds.filter(id => id !== optionId);
        } else {
          newSelectedIds = [...selectedIds, optionId];
        }
      }

      dispatch(
        setStepResponse({
          stepId: step.id,
          selectedIds: newSelectedIds,
        }),
      );
    };

    const handleToggleSelectAll = () => {
      if (allSelected) {
        dispatch(
          setStepResponse({
            stepId: step.id,
            selectedIds: [],
          }),
        );
      } else {
        dispatch(
          setStepResponse({
            stepId: step.id,
            selectedIds: options.map(opt => opt.id),
          }),
        );
      }
    };

    const canContinue =
      selectedIds.length > 0 &&
      (!minSelections || selectedIds.length >= minSelections) &&
      (!maxSelections || selectedIds.length <= maxSelections);

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
          {mcStep.question}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {options.map(option => {
            const isSelected = selectedIds.includes(option.id);

            return (
              <Chip
                key={option.id}
                label={option.label}
                onClick={() => handleToggle(option.id, option.label)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                icon={isSelected ? <CheckIcon /> : undefined}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  transition: theme.transitions.create(['all'], {
                    duration: theme.transitions.duration.short,
                  }),
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
                data-testid={`option-chip-${option.id}`}
              />
            );
          })}
        </Box>

        {/* Selection info and action button - same line */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {selectedIds.length > 0
                ? `${selectedIds.length} selected`
                : `Select ${minSelections ? `at least ${minSelections}` : 'options'}`}
            </Typography>

            {allowSelectAll && (
              <Button
                size="small"
                variant="text"
                onClick={handleToggleSelectAll}
                data-testid="select-all-button"
                sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </Box>

          {/* Action button */}
          {isLastStep ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleComplete}
              disabled={!canContinue || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Continue'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleNext}
              disabled={!canContinue || isProcessing}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  // Render confirmation step
  const renderConfirmationStep = (step: StepPayload) => {
    // Type assertion - we know this is a confirmation step
    const confStep = step as ConfirmationStepPayload;
    const confirmed = currentStepResponse?.confirmed;
    
    // Support both camelCase and snake_case
    const confirmLabel = confStep.confirmLabel ?? (confStep as any).confirm_label ?? 'Confirm';
    const cancelLabel = confStep.cancelLabel ?? (confStep as any).cancel_label ?? 'Cancel';

    const handleConfirm = () => {
      dispatch(
        setStepResponse({
          stepId: step.id,
          confirmed: true,
        }),
      );
    };

    const handleCancel = () => {
      dispatch(
        setStepResponse({
          stepId: step.id,
          confirmed: false,
        }),
      );
    };

    const canProceed = confirmed !== undefined;

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
          {confStep.message}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={isProcessing}
              color={confirmed === false ? 'error' : 'inherit'}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleConfirm}
              disabled={isProcessing}
              color={confirmed === true ? 'success' : 'primary'}
            >
              {confirmLabel}
            </Button>
          </Box>

          {/* Action button - only show when ready to proceed */}
          {isLastStep && canProceed && (
            <Button
              variant="contained"
              size="small"
              onClick={handleComplete}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Continue'}
            </Button>
          )}
          {!isLastStep && canProceed && (
            <Button
              variant="contained"
              size="small"
              onClick={handleNext}
              disabled={isProcessing}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        px: 2,
        pr: 5, // Extra right padding for dismiss button
        py: 1.5,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Top right: Step indicator and dismiss button */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {/* Step navigation */}
        <IconButton
          size="small"
          onClick={handlePrevious}
          disabled={isFirstStep || isProcessing}
          sx={{ 
            p: 0.25,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
          {currentStepIndex + 1}/{steps.length}
        </Typography>
        <IconButton
          size="small"
          onClick={handleNext}
          disabled={isLastStep || isProcessing}
          sx={{ 
            p: 0.25,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </IconButton>
        
        {/* Dismiss button */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={isProcessing}
          aria-label="Dismiss"
          sx={{
            p: 0.25,
            ml: 0.5,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Wizard Title */}
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
      )}

      {/* Current Step Content */}
      {getStepType(currentStep) === 'multiple_choice'
        ? renderMultipleChoiceStep(currentStep)
        : renderConfirmationStep(currentStep)}
    </Box>
  );
};

